import * as R from 'ramda';
import {
  IProductDetailsFiltersToCombinationsParams,
  IProductDetailsGroupSectionParams,
  ProductDetailsSummary,
} from '../../../../../services/modules/productDetails/productDetails.types';
import useGeneralSettingsStore from '../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { AvailabilityEnum } from '../../../../products/ProductTypes';
import {
  ProductDetailsDTO,
  ProductDetailsGroupSection,
} from '../../../../products/products-details-v2/types/productDetails.types';
import {
  availabilityToCompactAvailability,
  isPreOrder,
} from '../../../../products/utils/availability.utils';
import { OfflineDatabaseClassificationType } from '../../../database/offlineDatabase/types/offlineDatabaseClassificationType.types';
import { OfflineDatabaseDeliveryWindow } from '../../../database/offlineDatabase/types/offlineDatabaseDeliveryWindow.types';
import {
  OfflineDatabaseOrder,
  OfflineDatabaseOrderProduct,
} from '../../../database/offlineDatabase/types/offlineDatabaseOrder.types';
import { OfflineDatabaseProduct } from '../../../database/offlineDatabase/types/offlineDatabaseProduct.types';
import { OfflineDatabaseProductColor } from '../../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { offlineClassificationsRepository } from '../../../repositories/offlineClassifications.repository';
import { offlineDeliveryWindowsRepository } from '../../../repositories/offlineDeliveryWindows.repository';
import { offlineOrdersRepository } from '../../../repositories/offlineOrders.repository';
import { offlineProductColorsRepository } from '../../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../../repositories/offlineProducts.repository';
import {
  BaseCombination,
  Comparator,
  FormatCombinationParams,
  IsCombinationAvailableForCustomerParams,
  MountCombinationsWithOrdersParams,
  MountSetIdParams,
} from '../../types/productDetailsQuery.utils.types';
import { ValidateCatalogSectionItemForCustomerCatalogsUtils } from '../validateCatalogSectionItemForCustomerCatalogs.utils';

export default class ProductDetailsQueryUtils {
  static async getCombinations(
    params: IProductDetailsFiltersToCombinationsParams,
    restrictProductsUsingCustomerCatalogs = false,
  ): Promise<ProductDetailsDTO[]> {
    const data = await this.getCombinationsBase(params, restrictProductsUsingCustomerCatalogs);
    const ordered = this.orderCombinations(data, params);
    return ordered;
  }

  static async getCombinationsCount(
    params: IProductDetailsFiltersToCombinationsParams,
  ): Promise<ProductDetailsGroupSection[]> {
    const combinations = await this.getCombinationsBase(params);

    if (params.orderIds) {
      const grouped = R.groupBy((item: ProductDetailsDTO) => item.orderId || '', combinations);
      return Object.entries(grouped).map(([orderId, items]) => ({
        orderId,
        emptyOrderItems: [],
        productsCount: items?.length ?? 0,
      }));
    }

    return [{ emptyOrderItems: [], productsCount: combinations.length }];
  }

  static async getSummary(
    params: IProductDetailsGroupSectionParams,
  ): Promise<ProductDetailsSummary> {
    if (!params.orderId) {
      throw new Error('[getProductDetailsSummary] orderId is required in offline flow');
    }
    const order = await offlineOrdersRepository.getByIdOrThrow(params.orderId);
    const combinations = await this.getCombinationsBase(params);

    return this.getOrderSummaryBasedOnCombinations(order, combinations);
  }

  private static async getCombinationsBase(
    filter: IProductDetailsFiltersToCombinationsParams,
    restrictProductsUsingCustomerCatalogs = false,
  ): Promise<ProductDetailsDTO[]> {
    const productColors = await this.getProductColors(filter);
    const combinations: ProductDetailsDTO[] = [];
    const productIds = R.uniq(productColors.map((pc) => pc.productId));
    const products = await this.getProductsMap(productIds);
    const orderIds = filter.orderIds || [];

    for (const productColor of productColors) {
      const product = this.getProductFromMap(productColor, products);
      const availabilities = this.getAvailabilities(productColor, filter);
      for (const availability of availabilities) {
        const deliveryWindows = await this.getDeliveryWindows(
          productColor,
          availability,
          filter.deliveryWindowIds,
        );
        const formattedCombinations = await this.formatCombinations({
          product,
          orderIds,
          productColor,
          availability,
          deliveryWindows,
          restrictProductsUsingCustomerCatalogs,
        });
        combinations.push(...formattedCombinations);
      }
    }

    return combinations;
  }

  private static async isCombinationAvailableForCustomer({
    orderId,
    productColorId,
    restrictProductsUsingCustomerCatalogs,
  }: IsCombinationAvailableForCustomerParams) {
    const order = await offlineOrdersRepository.getByIdOrThrow(orderId);
    if (!order.customer) {
      return true;
    }

    return ValidateCatalogSectionItemForCustomerCatalogsUtils.validateForProductColor({
      customerId: order.customer.id,
      productColorId,
      restrictProductsUsingCustomerCatalogs,
    });
  }

  private static async getProductColors(
    params: IProductDetailsFiltersToCombinationsParams,
  ): Promise<OfflineDatabaseProductColor[]> {
    const productColors: OfflineDatabaseProductColor[] = [];

    if (params.productColorId) {
      const productColor = await offlineProductColorsRepository.getByIdOrFail(
        params.productColorId,
      );
      productColors.push(productColor);
    } else {
      const productColorsFromProduct = await this.getProductColorsFromProduct(params);
      productColors.push(...productColorsFromProduct);
    }

    if (params.filterByRelatedProducts) {
      const relatedProductColors = await this.getRelatedProductColors(productColors);
      productColors.push(...relatedProductColors);
    }

    return productColors;
  }

  private static async getProductColorsFromProduct(
    params: IProductDetailsFiltersToCombinationsParams,
  ): Promise<OfflineDatabaseProductColor[]> {
    if (!params.productId) {
      throw new Error('product id not provided to filter product colors');
    }
    return offlineProductColorsRepository.findByProductId(params.productId);
  }

  private static async getRelatedProductColors(
    productColors: OfflineDatabaseProductColor[],
  ): Promise<OfflineDatabaseProductColor[]> {
    const { suggestedClassificationType, enableRelatedProductsFilterInProductDetails } =
      useGeneralSettingsStore.getState().generalSettings;

    if (!enableRelatedProductsFilterInProductDetails || !suggestedClassificationType) {
      return [];
    }

    const { productColorIds, classificationIds } =
      this.getProductColorPropertiesToFindRelated(productColors);
    if (!classificationIds.length) {
      return [];
    }

    const classificationIdsToUse = await this.findClassificationsToUseAsFilter(
      classificationIds,
      suggestedClassificationType,
    );
    if (!classificationIdsToUse.length) {
      return [];
    }

    const relatedProductColors = await offlineProductColorsRepository.findRelatedProductColors(
      productColorIds,
      classificationIdsToUse,
    );

    return relatedProductColors;
  }

  private static getProductColorPropertiesToFindRelated(
    productColors: OfflineDatabaseProductColor[],
  ) {
    const productColorIds: string[] = [];
    const productColorClassificationIds: string[] = [];

    productColors.forEach((pc) => {
      productColorIds.push(pc.id);
      productColorClassificationIds.push(...pc.classificationIds);
    });

    const uniqueClassifications = R.uniq(productColorClassificationIds);
    return { classificationIds: uniqueClassifications, productColorIds };
  }

  private static async findClassificationsToUseAsFilter(
    classificationIds: string[],
    suggestedClassificationType: OfflineDatabaseClassificationType,
  ): Promise<string[]> {
    const classifications = await offlineClassificationsRepository.findByIdsAndClassificationTypeId(
      classificationIds,
      suggestedClassificationType.id,
    );
    return classifications.map((c) => c.id);
  }

  private static async getProductsMap(
    productIds: string[],
  ): Promise<Map<string, OfflineDatabaseProduct>> {
    const products = await offlineProductsRepository.findByIds(productIds);
    const productsMap = new Map<string, OfflineDatabaseProduct>();

    products.forEach((product) => {
      productsMap.set(product.id, product);
    });

    return productsMap;
  }

  private static getProductFromMap(
    productColor: OfflineDatabaseProductColor,
    products: Map<string, OfflineDatabaseProduct>,
  ): OfflineDatabaseProduct {
    const product = products.get(productColor.productId);
    if (!product) {
      throw new Error(`product not found for productColor [${productColor.id}]`);
    }
    return product;
  }

  private static getAvailabilities(
    productColor: OfflineDatabaseProductColor,
    filter: IProductDetailsFiltersToCombinationsParams,
  ): AvailabilityEnum[] {
    if (filter.availability) {
      return productColor.availabilities.filter(
        (productColorAvailability) =>
          filter.availability === availabilityToCompactAvailability(productColorAvailability) ||
          filter.availability === AvailabilityEnum.EMPTY,
      );
    }
    return productColor.availabilities;
  }

  private static async getDeliveryWindows(
    productColor: OfflineDatabaseProductColor,
    availability: AvailabilityEnum,
    deliveryWindowIdsToFilter?: string[],
  ): Promise<OfflineDatabaseDeliveryWindow[]> {
    switch (availability) {
      case AvailabilityEnum.INFINITE_PRE_ORDER:
        return this.getDeliveryWindowByIds(
          productColor.deliveryWindowIds.preOrder,
          deliveryWindowIdsToFilter,
        );

      case AvailabilityEnum.FUTURE_STOCK:
        return this.getDeliveryWindowByIds(
          productColor.deliveryWindowIds.futureStock,
          deliveryWindowIdsToFilter,
        );

      case AvailabilityEnum.READY_FOR_DELIVERY:
      default:
        return Promise.resolve([]);
    }
  }

  private static async getDeliveryWindowByIds(
    deliveryWindowIds: string[],
    deliveryWindowIdsToFilter?: string[],
  ): Promise<OfflineDatabaseDeliveryWindow[]> {
    const deliveryWindows = await offlineDeliveryWindowsRepository.findByIds(deliveryWindowIds);

    if (!deliveryWindowIdsToFilter || !deliveryWindowIdsToFilter.length) {
      return deliveryWindows;
    }

    const deliveryWindowIdsSet = new Set(deliveryWindowIdsToFilter);
    return deliveryWindows.filter((dw) => deliveryWindowIdsSet.has(dw.id));
  }

  private static async formatCombinations({
    product,
    orderIds,
    productColor,
    availability,
    deliveryWindows,
    restrictProductsUsingCustomerCatalogs = false,
  }: FormatCombinationParams): Promise<ProductDetailsDTO[]> {
    const combinations: ProductDetailsDTO[] = [];

    if (isPreOrder(availability) && !deliveryWindows.length) {
      return combinations;
    }

    const deliveryWindowsToUse = deliveryWindows.length ? deliveryWindows : [null];

    const baseCombination: BaseCombination = {
      availability,
      futureStockId: null,
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      productColorId: productColor.id,
      productColorName: productColor.name,
      productColorCode: productColor.code,
      productColorHexCode: productColor.hexcode,
      productColorIsImage: productColor.isImage,
      productColorImageUrl: productColor.imageUrl,
      allowChangePreOrderGrid: productColor.allowChangePreOrderGrid,
      allowChangeImmediateGrid: productColor.allowChangeImmediateGrid,
      productColorMediaUrl: productColor.medias.length ? productColor.medias[0].url : null,
    };

    for (const deliveryWindow of deliveryWindowsToUse) {
      const withDeliveryWindow: ProductDetailsDTO = {
        ...baseCombination,
        isAvailableForCustomer: true,
        futureStockDeliveryWindowId:
          availability === AvailabilityEnum.FUTURE_STOCK ? (deliveryWindow?.id ?? null) : null,
        deliveryWindowId: deliveryWindow?.id ?? null,
        deliveryWindowName: deliveryWindow?.name ?? null,
        deliveryWindowEndDate: deliveryWindow?.endDate.toString() ?? null,
        deliveryWindowStartDate: deliveryWindow?.startDate.toString() ?? null,
      };

      if (orderIds?.length) {
        await this.mountCombinationsWithOrders({
          orderIds,
          productColorId: productColor.id,
          combinations,
          currentCombinationWithDeliveryWindow: withDeliveryWindow,
          restrictProductsUsingCustomerCatalogs,
        });
      } else {
        combinations.push({ ...withDeliveryWindow });
      }
    }

    return combinations;
  }

  private static async mountCombinationsWithOrders({
    orderIds,
    productColorId,
    combinations,
    currentCombinationWithDeliveryWindow,
    restrictProductsUsingCustomerCatalogs,
  }: MountCombinationsWithOrdersParams) {
    for (const orderId of orderIds) {
      const isAvailableForCustomer = await this.isCombinationAvailableForCustomer({
        orderId,
        productColorId: productColorId,
        restrictProductsUsingCustomerCatalogs,
      });
      combinations.push({
        ...currentCombinationWithDeliveryWindow,
        orderId,
        isAvailableForCustomer,
      });
    }
  }

  private static orderCombinations(
    data: ProductDetailsDTO[],
    params: IProductDetailsFiltersToCombinationsParams,
  ): ProductDetailsDTO[] {
    const orderBySameFilterOrderIds: Comparator<ProductDetailsDTO>[] = params.orderIds
      ? [
          R.ascend((item) =>
            item.orderId ? (params.orderIds?.indexOf(item.orderId) ?? Infinity) : Infinity,
          ),
        ]
      : [];

    const relatedProductsFilterOrderBy: Comparator<ProductDetailsDTO>[] =
      params.filterByRelatedProducts
        ? [
            R.ascend((item) => (item.productId === params.productId ? 0 : 1)),
            R.ascend((item) => (item.productColorId === params.selectedProductColorId ? 0 : 1)),
            R.ascend((item) => item.productName.toLowerCase()),
          ]
        : [];

    const ordered = R.sortWith(
      [
        ...orderBySameFilterOrderIds,
        ...relatedProductsFilterOrderBy,
        R.ascend((item) => item.productColorName.toLowerCase()),
        R.ascend((item) => item.availability),
        R.ascend((item) => item.deliveryWindowStartDate || ''),
      ],
      data,
    );

    return ordered;
  }

  private static getOrderSummaryBasedOnCombinations(
    order: OfflineDatabaseOrder,
    combinations: ProductDetailsDTO[],
  ): ProductDetailsSummary {
    const orderProductsToSum = this.getOrderProductsThatAreInCombinations(order, combinations);
    return this.getSummaryBasedOnOrderProductsToSum(orderProductsToSum);
  }

  private static getOrderProductsThatAreInCombinations(
    order: OfflineDatabaseOrder,
    combinations: ProductDetailsDTO[],
  ): OfflineDatabaseOrderProduct[] {
    const combinationIdsSet = this.getCombinationIdsSet(combinations);

    const orderProductsThatAreInCombinations = order.products?.filter((orderProduct) => {
      const id = this.mountSetId({
        productColorId: orderProduct.productColor.id,
        availability: orderProduct.availability,
        deliveryWindowId: orderProduct.deliveryWindow?.id,
      });
      return combinationIdsSet.has(id);
    });

    return orderProductsThatAreInCombinations || [];
  }

  private static getCombinationIdsSet(combinations: ProductDetailsDTO[]): Set<string> {
    return new Set<string>(
      combinations.map((combination) =>
        this.mountSetId({
          productColorId: combination.productColorId,
          availability: combination.availability,
          deliveryWindowId: combination.deliveryWindowId,
        }),
      ),
    );
  }

  private static mountSetId({ productColorId, availability, deliveryWindowId }: MountSetIdParams) {
    return `${productColorId}-${availability}-${deliveryWindowId ?? ''}`;
  }

  private static getSummaryBasedOnOrderProductsToSum(
    orderProducts: OfflineDatabaseOrderProduct[],
  ): ProductDetailsSummary {
    let totalItems = 0;
    let totalPieces = 0;
    let subTotal = 0;

    orderProducts.forEach((orderProduct) => {
      totalItems += 1;

      orderProduct.grid.forEach((gridItem) => {
        totalPieces += gridItem.quantity;
      });

      const priceInfo = orderProduct.metadata.productColorPriceInfo;

      priceInfo.version === 2 &&
        priceInfo.pricesToSizes.forEach((priceToSize) => {
          const price = Math.min(
            priceToSize.wholesalePrice,
            priceToSize.promotionalPrice ?? Infinity,
          );

          const gridItem = orderProduct.grid.find(
            (gridItem) => gridItem.size.id === priceToSize.sizeId,
          );

          subTotal += price * (gridItem?.quantity ?? 0);
        });
    });

    return { subTotal, totalItems, totalPieces };
  }
}
